// LWC applies viewport writes on its rendering frame. Retain keyboard intent
// between frames so each tap advances from the previous requested viewport.
const viewports = new WeakMap()
const sameRange = (a, b) => a && b && Math.abs(a.from - b.from) < 1e-6 && Math.abs(a.to - b.to) < 1e-6
const ZOOM_FACTOR = 0.7
const VIEWPORT_DURATION_MS = 140

function animateViewport(chart, state, rendered, target) {
  if (state.frame != null) cancelAnimationFrame(state.frame)
  const started = performance.now()
  state.animating = true
  const tick = (now) => {
    state.frame = null
    if (viewports.get(chart) !== state) return
    try {
      const progress = Math.min(1, Math.max(0, (now - started) / VIEWPORT_DURATION_MS))
      const eased = 1 - (1 - progress) ** 3
      state.written = {
        from: rendered.from + (target.from - rendered.from) * eased,
        to: rendered.to + (target.to - rendered.to) * eased,
      }
      state.scale.setVisibleLogicalRange(state.written)
      // A native clamp or external range change may cancel inside the setter.
      if (viewports.get(chart) !== state) return
      if (progress < 1) state.frame = requestAnimationFrame(tick)
      else {
        state.animating = false
        // Keep the target until LWC has committed the final rendering frame.
        state.frame = requestAnimationFrame(() => { state.pending = null; state.frame = null })
      }
    } catch { releaseChartViewport(chart) }
  }
  state.frame = requestAnimationFrame(tick)
}

export function releaseChartViewport(chart) {
  const state = chart && viewports.get(chart)
  if (!state) return
  viewports.delete(chart)
  if (state.frame != null) cancelAnimationFrame(state.frame)
  try { state.scale.unsubscribeVisibleLogicalRangeChange?.(state.onRange) } catch { /* already disposed */ }
}

// UI-only navigation through LWC's public logical-range API. No series writes.
export function moveChartViewport(chart, action, latestTime) {
  if (!chart) return { ok: false, reason: 'chart_unavailable' }
  try {
    const scale = chart.timeScale()
    const rendered = scale.getVisibleLogicalRange()
    let state = viewports.get(chart)
    if (!state) {
      state = { scale, pending: null, rendered: null, frame: null }
      // Our own animation frames preserve the destination. Wheel/drag,
      // history replacement and native clamping supersede keyboard intent.
      state.onRange = (range) => {
        if (state.animating || state.written) {
          if (sameRange(range, state.written)) return
          releaseChartViewport(chart)
        } else state.pending = null
      }
      scale.subscribeVisibleLogicalRangeChange?.(state.onRange)
      viewports.set(chart, state)
    }
    const range = state.pending && (state.animating || sameRange(rendered, state.written) || sameRange(rendered, state.rendered)) ? state.pending : rendered
    if (!range || !Number.isFinite(range.from) || !Number.isFinite(range.to) || range.to <= range.from) {
      return { ok: false, reason: 'chart_empty' }
    }
    const span = range.to - range.from
    let next
    if (action === 'left' || action === 'right') {
      const shift = Math.max(1, span * 0.1) * (action === 'left' ? -1 : 1)
      next = { from: range.from + shift, to: range.to + shift }
    } else if (action === 'in' || action === 'out') {
      const latest = latestTime == null ? null : scale.timeToIndex(latestTime, true)
      // Keep the latest visible candle at the same screen position. When
      // reviewing history, keep the rightmost visible bar instead of jumping.
      const anchor = Number.isFinite(latest) && latest >= range.from && latest <= range.to ? latest : range.to
      const nextSpan = Math.max(2, Math.min(100000, span * (action === 'in' ? ZOOM_FACTOR : 1 / ZOOM_FACTOR)))
      const ratio = nextSpan / span
      next = { from: anchor + (range.from - anchor) * ratio, to: anchor + (range.to - anchor) * ratio }
    } else return { ok: false, reason: 'unknown_viewport_action' }
    state.rendered = rendered && { ...rendered }
    state.pending = next
    const smooth = typeof requestAnimationFrame === 'function'
      && !globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (smooth) {
      animateViewport(chart, state, rendered, next)
      return { ok: true }
    }
    if (state.frame != null) cancelAnimationFrame(state.frame)
    state.frame = null
    state.animating = false
    state.written = null
    scale.setVisibleLogicalRange(next)
    // A clamped no-op may emit no range event. LWC's draw was scheduled by
    // the setter above; retire intent after that frame even at a hard edge.
    if (state.frame == null && typeof requestAnimationFrame === 'function') {
      state.frame = requestAnimationFrame(() => { state.pending = null; state.frame = null })
    }
    return { ok: true }
  } catch {
    releaseChartViewport(chart)
    // A queued command may reach a chart whose native canvas was disposed.
    return { ok: false, reason: 'chart_unavailable' }
  }
}
